import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ExternalLink, BookOpen, Download, Share2, UserCheck, MessageCircle } from "lucide-react";
import { API, api, shareImage, openExternal, setRunnerToken } from "../lib/api";
import { PageShell, DISCLAIMER } from "../components/PageShell";
import { AppText, Button, Card, Pastille, Toggle } from "../components/ui";
import { Seo } from "../components/Seo";

const MOTOR = {
  terrien: { title: "Terrien", emoji: "🛞", text: "Ta force vient plutôt du sol et de l'ancrage, comme une roue qui déroule. Aux allures de débutant, tout le monde déroule le pied : ton programme ne te donnera aucune consigne technique sans une vraie analyse de foulée." },
  aerien: { title: "Aérien", emoji: "🦘", text: "Ta force vient plutôt du ressort et du rebond. Aux allures de débutant, presque tout le monde déroule le pied ; seuls les aériens de pieds restent sur l'avant. Aucune consigne technique sans une vraie analyse de foulée." },
  intermediaire: { title: "Intermédiaire", emoji: "⚖️", text: "Le questionnaire ne tranche pas, comme pour beaucoup de coureurs. Ton programme reste le même : silence des appuis et se grandir, sans consigne technique." },
};
const STAB = {
  solide: { title: "Solide", text: "Ton bassin tient." },
  a_renforcer: { title: "À renforcer", text: "10 minutes de gainage debout avant chaque sortie vont tout changer." },
  fragile: { title: "Fragile", text: "On commence par là. Ton programme démarre par le renforcement." },
};
const SHOES = { coherente: "Cohérentes", a_surveiller: "À surveiller", incoherente: "Incohérentes", a_verifier: "À vérifier" };
const B2_OPTIONS = [
  { value: "je_ne_sais_pas", label: "Je ne sais pas encore" },
  { value: "moins_5", label: "Moins de 5 minutes" },
  { value: "5_15", label: "5 à 15 minutes" },
  { value: "15_30", label: "15 à 30 minutes" },
  { value: "plus", label: "Plus de 30 minutes" },
];
const TRIAL_DAYS_LABEL = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export default function Resultat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useQuery({ queryKey: ["profile", id], queryFn: () => api.getProfile(id), enabled: !!id, retry: false });
  const { data: config } = useQuery({ queryKey: ["config"], queryFn: api.getConfig });

  return (
    <>
      <Seo title="Ton profil de coureur" description="Le résultat de ton profil de coureur KM01." noindex />
      <PageShell>
        {isLoading || !data ? (
          <div className="py-20 flex justify-center">
            {isLoading ? <span className="h-8 w-8 animate-spin rounded-full border-4 border-km-coral border-t-transparent" /> : <AppText>Profil introuvable.</AppText>}
          </div>
        ) : (
          <Inner data={data} config={config} navigate={navigate} refetch={refetch} />
        )}
      </PageShell>
    </>
  );
}

function Inner({ data, config, navigate, refetch }) {
  const r = data.result;
  const [paying, setPaying] = useState(null);
  const [trialOpen, setTrialOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [tEmail, setTEmail] = useState(data.email || "");
  const [age, setAge] = useState("");
  const [b2, setB2] = useState(null);
  const [b10, setB10] = useState(null);
  const [tDays, setTDays] = useState([]);
  const toggleTDay = (d) => setTDays((p) => p.includes(d) ? p.filter((x) => x !== d) : [...p, d].sort((a, b) => a - b));
  useEffect(() => { try { localStorage.setItem("km1_last_profile", data.id); } catch (e) {} }, [data.id]);

  const startTrial = async () => {
    if (!/\S+@\S+\.\S+/.test(tEmail.trim())) { toast.error("Indique un email valide pour recevoir ton accès"); return; }
    if (!tDays.length) { toast.error("Choisis au moins un jour d'entraînement : sans ta semaine type, pas de programme possible"); return; }
    setStarting(true);
    try {
      const payload = { training_days: tDays };
      const n = parseInt(age, 10);
      if (n >= 10 && n <= 100) payload.age = n;
      if (b2) payload.b2 = b2;
      if (b10) payload.b10 = b10;
      try { await api.completeProfile(data.id, payload); } catch (e) {}
      const res = await api.startTrial(data.id, tEmail.trim());
      if (res.session_token) {
        setRunnerToken(res.session_token);
        toast.success(`C'est parti — ${res.days} jours offerts !`);
        navigate("/programme");
        return;
      }
      setStarting(false);
    } catch (e) {
      toast.error(e.message || "Impossible de démarrer ton essai");
      setStarting(false);
    }
  };
  const pay = async (plan) => {
    setPaying(plan);
    try {
      const res = await api.checkout(data.id, plan, data.email || undefined, localStorage.getItem("km1_ref") || undefined);
      if (res.url) { window.location.assign(res.url); return; }
      navigate(`/programme?profile=${data.id}&plan=${plan}`);
    } catch (e) {
      toast.error(e.message || "Paiement indisponible, réessaie");
      navigate(`/programme?profile=${data.id}&plan=${plan}`);
    } finally { setPaying(null); }
  };
  const errors = r.errors || r.top_errors || data.top_errors || [];
  const sex = data.answers?.A2;
  const m = MOTOR[r.motor.label] || MOTOR.intermediaire;
  const motorTitle = sex === "femme" && r.motor.label !== "intermediaire" ? m.title + "ne" : m.title;
  const stab = STAB[r.stability.band];
  const stabColor = r.stability.band === "solide" ? "#7BAE8C" : "#FF6B4A";
  const shoesBad = r.shoes.verdict === "incoherente" || r.shoes.verdict === "a_verifier";
  const fullAnalysis = config?.full_analysis_url || "https://liberetafoulee.fr";
  const bookUrl = config?.book_url || "https://www.amazon.fr/dp/B0HGQH4S9N";
  const storyUrl = `${API}/profiles/${data.id}/story.png`;
  const pdfUrl = `${API}/profiles/${data.id}/report.pdf`;

  const share = async () => {
    try {
      const res = await shareImage(storyUrl, "km01-mon-profil.png", "Mon profil de coureur KM01");
      if (res === "downloaded") toast.success("Image story téléchargée (1080×1920)");
    } catch { toast.error("Partage impossible"); }
  };

  return (
    <div className="pt-6 flex flex-col gap-4">
      <AppText variant="display" testID="result-title">Voici ton profil de coureur{data.first_name ? `, ${data.first_name}` : ""}</AppText>

      {/* Profil moteur */}
      <Card className="flex flex-col items-center gap-3" testID="card-motor">
        <Pastille value={motorTitle.slice(0, 1)} size={88} />
        <span className="text-[56px] leading-none">{m.emoji}</span>
        <AppText variant="label" className="text-km-muted">Probablement</AppText>
        <AppText variant="display" className="text-km-coral" testID="motor-title">{motorTitle}{r.motor.subtype ? <span className="text-km-ink"> de {r.motor.subtype}</span> : null}</AppText>
        {r.motor.score != null ? <AppText variant="caption" className="text-km-muted" testID="motor-score">Estimation d'après tes réponses ({String(r.motor.score).replace(".", ",")} / 10) — {r.motor.subtype === "pieds" ? "plutôt pieds et mollets" : "plutôt cuisses et fessiers"}. Seule une analyse de foulée sur liberetafoulee.fr peut la confirmer.</AppText> : null}
        <AppText variant="body" className="text-center">{m.text}</AppText>
        <AppText variant="caption" className="text-km-muted text-center">Ce questionnaire donne une <strong>forte probabilité, jamais une certitude</strong> : un terrien « de pied » et un aérien « de cuisse » se ressemblent beaucoup. Pour valider ton profil moteur, fais l'analyse de foulée sur liberetafoulee.fr.</AppText>
        <Button title="Confirmer sur liberetafoulee.fr" variant="secondary" size="md" onClick={() => openExternal(fullAnalysis)} testID="motor-analysis-link" icon={<ExternalLink size={18} strokeWidth={2.5} />} />
      </Card>

      {/* Stabilité */}
      <Card testID="card-stability">
        <AppText variant="label" className="text-km-muted">Stabilité</AppText>
        <div className="flex items-end gap-2 mt-1">
          <span className="font-display font-extrabold text-[34px] leading-[40px]" style={{ color: stabColor }}>{r.stability.score}</span>
          <AppText variant="subtitle" className="text-km-muted mb-1.5">/10 · {stab.title}</AppText>
        </div>
        <div className="h-3.5 rounded-full bg-km-border mt-3 overflow-hidden" aria-label={`Stabilité ${r.stability.score} sur 10`}>
          <div className="h-3.5 rounded-full" style={{ width: `${r.stability.score * 10}%`, backgroundColor: stabColor }} data-testid="stability-gauge" />
        </div>
        <AppText variant="body" className="mt-3">{stab.text}</AppText>
      </Card>

      {/* Chaussures */}
      <Card testID="card-shoes">
        <AppText variant="label" className="text-km-muted">Chaussures{data.shoe?.label ? ` · ${data.shoe.label}` : ""}</AppText>
        <AppText variant="display" className="mt-1" style={{ color: shoesBad ? "#FF6B4A" : "#7BAE8C" }}>{SHOES[r.shoes.verdict]}</AppText>
        <AppText variant="body" className="text-km-ontertiary">
          {r.shoes.reason.charAt(0).toUpperCase() + r.shoes.reason.slice(1)}
          {r.shoes.dynamic_drop !== null && r.shoes.dynamic_drop !== undefined ? ` (drop dynamique ${r.shoes.dynamic_drop} mm)` : ""}.
        </AppText>
        {data.shoe?.custom ? (
          <div className="mt-3 rounded-2xl bg-km-tertiary p-3 flex flex-col gap-2" data-testid="shoe-expert-block">
            <AppText variant="caption" className="text-km-ontertiary">Ce modèle n'est pas encore dans notre base : le verdict ci-dessus est estimé. Demande l'avis du coach, il vérifie tes chaussures personnellement.</AppText>
            <Button title="Demander l'avis du coach" variant="secondary" size="md" onClick={() => navigate("/programme")} testID="shoe-expert-link" icon={<MessageCircle size={18} strokeWidth={2.5} />} />
          </div>
        ) : null}
      </Card>

      {/* 3 erreurs */}
      <Card testID="card-errors">
        <AppText variant="subtitle">Les 3 erreurs qu'on t'a peut-être déjà conseillées</AppText>
        <div className="flex flex-col gap-4 mt-3">
          {errors.map((e) => (
            <div key={e.number}>
              <AppText variant="bodyStrong" className="text-km-muted line-through">« {e.phrase} »</AppText>
              <AppText variant="bodyStrong" className="mt-0.5">{e.answer}</AppText>
            </div>
          ))}
        </div>
        <div className="mt-4"><Button title="Partager" variant="secondary" size="md" onClick={share} testID="share-button" icon={<Share2 size={18} strokeWidth={2.5} />} /></div>
      </Card>

      {/* Cadence */}
      {r.cadence ? (
        <Card testID="card-cadence">
          <AppText variant="label" className="text-km-muted">Cadence théorique naturelle</AppText>
          <AppText variant="body" className="mt-1" testID="cadence-text">
            Ta cadence théorique naturelle se situe entre <strong>{r.cadence.min ?? r.cadence.value} et {r.cadence.max ?? r.cadence.value + 10} pas par minute</strong>. Ce n'est pas une consigne, c'est un repère. Ne cherche pas à la changer.
          </AppText>
        </Card>
      ) : null}

      {/* Relecture coach */}
      {r.coach_review ? (
        <div className="flex items-center gap-3 bg-km-sage rounded-km p-4" data-testid="coach-review-box">
          <UserCheck size={26} className="text-white shrink-0" />
          <AppText variant="body" className="text-white flex-1">Ton profil sort un peu du cadre : l'entraîneur y jettera un œil et interviendra s'il a un doute. Tu peux commencer.</AppText>
        </div>
      ) : null}

      {/* Conversion — essai gratuit 15 jours en avant */}
      <Card className="border-2 border-km-coral" testID="card-conversion">
        <AppText variant="title">Ton programme KM01 est prêt.</AppText>
        <AppText variant="body" className="mt-2">
          3 séances par semaine, adaptées à ton profil <strong>{motorTitle.toLowerCase()}</strong>, ta stabilité <strong>{stab.title.toLowerCase()}</strong> et tes chaussures. Tu démarres en <strong>phase {r.phase}</strong>.
        </AppText>

        {trialOpen ? (
          <div className="mt-4 flex flex-col gap-4 rounded-2xl bg-km-sage/10 border border-km-sage p-4" data-testid="trial-form">
            <AppText variant="subtitle">Encore 3 petites questions, et c'est parti</AppText>
            <div>
              <AppText variant="label" className="text-km-coral">Ton âge (facultatif)</AppText>
              <input value={age} onChange={(e) => setAge(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" placeholder="ex. 42" data-testid="trial-age"
                className="mt-2 max-w-[140px] min-h-[48px] w-full bg-white rounded-xl border-2 border-km-border px-3 font-text text-[16px] text-km-ink outline-none focus:border-km-coral" />
            </div>
            <div>
              <AppText variant="label" className="text-km-coral">Aujourd'hui, tu cours sans t'arrêter pendant…</AppText>
              <div className="flex flex-col gap-2 mt-2">
                {B2_OPTIONS.map((o) => (
                  <button key={o.value} onClick={() => setB2(o.value)} type="button" data-testid={`trial-b2-${o.value}`}
                    className={`min-h-[48px] rounded-xl border-2 px-4 text-left font-text font-bold text-[15px] ${b2 === o.value ? "border-km-coral bg-km-coralt text-km-oncoralt" : "border-km-border bg-white text-km-ink"}`}>{o.label}</button>
                ))}
              </div>
            </div>
            <div>
              <AppText variant="label" className="text-km-coral">Un autre sport d'endurance régulier (foot, vélo, natation, fitness…) ou déjà couru régulièrement ?</AppText>
              <div className="flex gap-2 mt-2">
                {[["oui", "Oui"], ["non", "Non"]].map(([v, l]) => (
                  <button key={v} onClick={() => setB10(v)} type="button" data-testid={`trial-b10-${v}`}
                    className={`min-h-[44px] px-5 rounded-full border-2 font-text font-bold text-[15px] ${b10 === v ? "border-km-coral bg-km-coralt text-km-oncoralt" : "border-km-border bg-white text-km-ink"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <AppText variant="label" className="text-km-coral">Tes jours d'entraînement (jusqu'à 7) — indispensable pour construire ta semaine</AppText>
              <div className="flex flex-wrap gap-2 mt-2">
                {TRIAL_DAYS_LABEL.map((n, d) => (
                  <button key={d} onClick={() => toggleTDay(d)} type="button" data-testid={`trial-day-${d}`}
                    className={`px-3 min-h-[44px] rounded-full border-2 font-text font-bold text-[14px] ${tDays.includes(d) ? "border-km-coral bg-km-coralt text-km-oncoralt" : "border-km-border bg-white text-km-ink"}`}>{n}</button>
                ))}
              </div>
            </div>
            <div>
              <AppText variant="label" className="text-km-coral">Ton email — pour recevoir ton accès</AppText>
              <input value={tEmail} onChange={(e) => setTEmail(e.target.value)} type="email" placeholder="ton@email.fr" data-testid="trial-email"
                className="mt-2 min-h-[52px] w-full bg-white rounded-2xl border-2 border-km-border px-4 font-text font-semibold text-[17px] text-km-ink outline-none focus:border-km-coral" />
            </div>
            <Button title="Démarrer mon mois gratuit" onClick={startTrial} loading={starting} testID="trial-confirm" className="w-full" />
            <AppText variant="caption" className="text-km-muted text-center">Aucune carte bancaire. On te proposera l'abonnement plus tard, seulement si le programme te plaît.</AppText>
          </div>
        ) : (
          <div className="mt-4 rounded-2xl bg-km-sage/10 border border-km-sage p-4 flex flex-col gap-2">
            <AppText variant="subtitle">Programme ESSAI GRATUIT — 1 mois</AppText>
            <AppText variant="body" className="text-km-ontertiary">Sans carte bancaire, non renouvelable. Tu réponds à 3 petites questions et ton programme hebdomadaire démarre tout de suite.</AppText>
            <Button title="Oui, je commence mon mois gratuit" onClick={() => setTrialOpen(true)} testID="trial-open" className="mt-1 w-full" />
          </div>
        )}

        {/* Abonnement direct — secondaire */}
        <div className="mt-5 pt-4 border-t border-km-divider">
          <AppText variant="caption" className="text-km-muted">Tu préfères viser directement ton objectif ?</AppText>
          <Button title="Programme 10K — 3 mois, 14,90 €" variant="secondary" onClick={() => pay("3m")} loading={paying === "3m"} testID="cta-3m" className="mt-2 w-full" />
          <Button title="Programme 21/42K — 6 mois, 24,90 €" variant="secondary" onClick={() => pay("6m")} loading={paying === "6m"} testID="cta-6m" className="mt-2 w-full" />
        </div>

        <AppText variant="caption" className="text-km-muted mt-3 leading-5">Programme réalisé par IA selon la méthode « Libère ta foulée » d'Éric, entraîneur depuis 1995. Les cas qui le nécessitent sont relus personnellement.</AppText>
        <button onClick={() => openExternal(bookUrl)} data-testid="book-link" className="flex items-center gap-2 min-h-[44px] mt-1">
          <BookOpen size={18} className="text-km-coral" fill="currentColor" stroke="#fff" />
          <AppText variant="caption" className="text-km-coral font-bold">Le livre pour aller plus loin : Libère ta foulée</AppText>
        </button>
      </Card>

      {/* Email / PDF */}
      {data.email ? (
        <Card className="flex flex-col gap-2" testID="card-email-done">
          <AppText variant="body">📩 Ton rapport a été envoyé à <strong>{data.email}</strong>. Ce lien reste valable à vie.</AppText>
          <Button title="Télécharger le PDF" variant="secondary" size="md" onClick={() => openExternal(pdfUrl)} testID="pdf-button" icon={<Download size={18} strokeWidth={2.5} />} />
        </Card>
      ) : (
        <EmailCapture id={data.id} onDone={refetch} />
      )}

      <AppText variant="caption" className="text-km-muted leading-5">{DISCLAIMER}</AppText>
    </div>
  );
}

function EmailCapture({ id, onDone }) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!/\S+@\S+\.\S+/.test(email.trim())) { toast.error("Cet email ne semble pas valide"); return; }
    setLoading(true);
    try {
      await api.attachEmail(id, email.trim(), consent);
      toast.success("Rapport envoyé !");
      onDone();
    } catch (e) { toast.error(e.message || "Erreur"); } finally { setLoading(false); }
  };
  return (
    <Card className="flex flex-col gap-2" testID="card-email">
      <AppText variant="subtitle">Reçois ton rapport PDF par email</AppText>
      <AppText variant="caption" className="text-km-muted">Ton profil reste consultable à vie via le lien envoyé.</AppText>
      <input data-testid="result-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="ton@email.fr"
        className="min-h-[52px] bg-km-tertiary rounded-2xl border-2 border-km-border px-4 font-text font-semibold text-[17px] text-km-ink outline-none focus:border-km-coral" />
      <button onClick={() => setConsent(!consent)} type="button" className="flex items-center gap-3 min-h-[44px] text-left">
        <Toggle checked={consent} />
        <AppText variant="caption" className="text-km-ontertiary flex-1">J'accepte de recevoir des conseils de KM01 (désinscription en un clic).</AppText>
      </button>
      <Button title="Recevoir mon rapport" onClick={submit} loading={loading} testID="result-email-submit" size="md" />
    </Card>
  );
}
